import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { MoreHorizontal, Printer, Receipt, RotateCcw, Eye } from "lucide-react";

/**
 * Delivery — Ενέργειες button for closed orders (DELIVERED / CANCELLED).
 *
 * Surfaces 4 follow-up actions that don't apply to active orders:
 *   1. «Προεπισκόπηση» — read-only modal with items + customer.
 *   2. «Επανεκτύπωση» — re-prints the order proforma (76-mm thermal).
 *   3. «Απόδειξη»      — opens the payment dialog so the order can be
 *                        sent to the cash-register (fiscal print).
 *   4. «Επανάληψη»     — opens a NEW order pre-loaded with the same
 *                        items, comments and customer.
 *
 *   onPreview / onReprint / onReceipt / onRepeat are required.
 *   `size` ("sm" | "md") controls density — "sm" is used in the table
 *   row, "md" in the card variant.
 */
export default function DeliveryActionsMenu({
  order,
  onPreview,
  onReprint,
  onReceipt,
  onRepeat,
  size = "md",
  testid,
}) {
  const triggerCls =
    size === "sm"
      ? "h-10 px-3 text-sm"
      : "h-12 px-5 text-sm";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={`${triggerCls} font-bold border-2 border-slate-300 hover:bg-slate-100`}
          data-testid={testid || `delivery-actions-${order?.order_no}`}
        >
          <MoreHorizontal size={size === "sm" ? 16 : 18} className="mr-1.5" />
          Ενέργειες
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 p-2"
        data-testid={`delivery-actions-menu-${order?.order_no}`}
      >
        <button
          type="button"
          onClick={() => onPreview?.(order)}
          className="w-full flex items-center gap-3 px-4 py-3.5 rounded-md text-base font-bold text-slate-800 hover:bg-slate-100 active:scale-[0.98] transition-all"
          data-testid={`delivery-action-preview-${order?.order_no}`}
        >
          <Eye size={20} className="text-slate-600 shrink-0" />
          Προεπισκόπηση
        </button>
        <button
          type="button"
          onClick={() => onReprint?.(order)}
          className="w-full flex items-center gap-3 px-4 py-3.5 rounded-md text-base font-bold text-slate-800 hover:bg-blue-50 active:scale-[0.98] transition-all"
          data-testid={`delivery-action-reprint-${order?.order_no}`}
        >
          <Printer size={20} className="text-blue-600 shrink-0" />
          Επανεκτύπωση
        </button>
        <button
          type="button"
          onClick={() => onReceipt?.(order)}
          className="w-full flex items-center gap-3 px-4 py-3.5 rounded-md text-base font-bold text-slate-800 hover:bg-emerald-50 active:scale-[0.98] transition-all"
          data-testid={`delivery-action-receipt-${order?.order_no}`}
        >
          <Receipt size={20} className="text-emerald-600 shrink-0" />
          Απόδειξη
        </button>
        <button
          type="button"
          onClick={() => onRepeat?.(order)}
          className="w-full flex items-center gap-3 px-4 py-3.5 rounded-md text-base font-bold text-slate-800 hover:bg-indigo-50 active:scale-[0.98] transition-all"
          data-testid={`delivery-action-repeat-${order?.order_no}`}
        >
          <RotateCcw size={20} className="text-indigo-600 shrink-0" />
          Επανάληψη
        </button>
      </PopoverContent>
    </Popover>
  );
}
